import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import ActionButton from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import { CalendarTwoIcon, DeleteIcon, PenIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { getCountries } from '../../../../api/countries/get-countries'
import { getFormulas } from '../../../../api/formula/get-formulas'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { AdminStaffInput } from '../../../../lib/types/models/staff/admin-staff-input'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledMultiSelectInput from '../../../elements/inputs/controlled-multi-select-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import PeriodBadge from '../../../elements/period-badge'
import { StaffAvailabilityForm } from '../../staffs/staff-availability-form'

const StaffAvailabilitiesSection = () => {
  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'edit',
    number
  >(['create', 'edit'])

  const form = useFormContext<AdminStaffInput>()
  const availabilities = form.watch('availabilities')
  const position = form.watch('position')

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  const availabilityError = form.formState.errors.availabilities

  return (
    <div>
      <div className="w-full flex justify-end">
        <Button
          icon={PlusIcon}
          type={'button'}
          variant={'secondary'}
          onClick={() => toggleModal('create')}
          label={'Ajouter une disponibilité'}
        />
      </div>

      <FormField
        name={'isAvailableForTheYear'}
        render={({ field }) => (
          <div className={'flex w-full items-center gap-16'}>
            <FormItem>
              <FormControl>
                <ControlledRadioInput
                  {...field}
                  label={'Disponible'}
                  checked={field.value === true ? true : false}
                  onCheck={() => field.onChange(true)}
                />
              </FormControl>
            </FormItem>
            <FormItem>
              <FormControl>
                <ControlledRadioInput
                  {...field}
                  label={'Indisponible cette année '}
                  checked={field.value === false ? true : false}
                  onCheck={() => field.onChange(false)}
                />
              </FormControl>
            </FormItem>
          </div>
        )}
      />

      <div className="h-4" />

      <div className={'flex flex-col gap-4'}>
        {availabilities?.map((availability, index) => (
          <div key={index} className="flex flex-col gap-2">
            <div
              className={cn('flex flex-col gap-2', {
                'border border-red p-2 rounded-lg': availabilityError && availabilityError[index]
              })}
            >
              <div className={'flex flex-row justify-between items-center '}>
                <div className="w-60 flex flex-wrap gap-2">
                  {availability.periods?.map((period) => (
                    <PeriodBadge key={period.id} period={period} variant="warning" />
                  ))}
                </div>
                <div className={'flex gap-3 flex-1 items-center'}>
                  <div className="flex gap-3 p-3 border rounded-sm border-grey-light w-full">
                    <Typography variant="label" color={tailwindColors.grey.light.active}>
                      De
                    </Typography>
                    <Typography variant="label">
                      {DateFormatter.DDMMYYYY(availability.startDate)}
                    </Typography>
                  </div>
                  <div className="flex gap-3 p-3 border rounded-sm border-grey-light w-full">
                    <Typography variant="label" color={tailwindColors.grey.light.active}>
                      À
                    </Typography>
                    <Typography variant="label">
                      {DateFormatter.DDMMYYYY(availability.endDate)}
                    </Typography>
                  </div>

                  <div className="flex gap-3">
                    <ActionButton
                      variant={'grey'}
                      icon={PenIcon}
                      onClick={() => toggleModal('edit', index)}
                    />

                    <ActionButton
                      variant={'danger'}
                      icon={DeleteIcon}
                      onClick={() =>
                        form.setValue(
                          'availabilities',
                          form.getValues().availabilities.filter((_, i) => i !== index)
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
            {availabilityError && availabilityError[index] && (
              <Typography variant="small" color={tailwindColors.red.DEFAULT}>
                {availabilityError[index]?.startDate?.message}
              </Typography>
            )}
          </div>
        ))}
      </div>

      <div className="h-4" />
      <div className="flex gap-4">
        <FormField
          control={form.control}
          name="countriesWishListIds"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledMultiSelectInput
                  {...field}
                  variant="primary"
                  label="Liste de pays souhaités"
                  options={
                    countries?.data.map((country) => ({
                      label: country.name,
                      value: country.id
                    })) ?? []
                  }
                  selectedOptions={field.value?.map((id) => ({
                    label: countries?.data.find((country) => country.id === id)?.name ?? '',
                    value: id
                  }))}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="formulasWishListIds"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledMultiSelectInput
                  {...field}
                  variant="primary"
                  label="Liste de formules souhaitées"
                  options={
                    formulas?.data.map((formula) => ({
                      label: getTranslatedName(formula),
                      value: formula.id
                    })) ?? []
                  }
                  selectedOptions={field.value?.map((id) => ({
                    label: getTranslatedName(formulas?.data.find((formula) => formula.id === id)),
                    value: id
                  }))}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <Modal
        isOpen={modalState.create}
        title={'Disponibilités'}
        icon={CalendarTwoIcon}
        hideActions
        onCancel={() => closeModel('create')}
      >
        <StaffAvailabilityForm
          className={MODAL_BODY_CLASSNAME}
          submitText={'Ajouter'}
          handleSubmit={(values) => {
            form.setValue('availabilities', [...form.getValues().availabilities, values])
            closeModel('create')
          }}
          onCancel={() => closeModel('create')}
          position={position}
        />
      </Modal>

      {selectedData !== undefined && (
        <Modal
          isOpen={modalState.edit}
          title={'Disponibilités'}
          icon={CalendarTwoIcon}
          hideActions
          onCancel={() => closeModel('edit')}
        >
          <StaffAvailabilityForm
            className={MODAL_BODY_CLASSNAME}
            submitText={'Modifier'}
            handleSubmit={(values) => {
              form.setValue(
                'availabilities',
                [
                  ...form.getValues().availabilities.slice(0, selectedData),
                  values,
                  ...form.getValues().availabilities.slice(selectedData + 1)
                ],
                { shouldValidate: true }
              )
              closeModel('edit')
            }}
            defaultValues={availabilities[selectedData]}
            onCancel={() => closeModel('edit')}
            position={position}
          />
        </Modal>
      )}
    </div>
  )
}

export default StaffAvailabilitiesSection
